import { useDisclosure } from "@shared/hooks/useDisclosure";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import ScrollContainer from "@shared/components/ui-kit/layout/ScrollContainer/ScrollContainer.web";
import { ASSIGNED_SEATING } from "@shared/constants";
import { useEffect, useState } from "react";
import { type BestAvailableSeatingProps } from "../../../components/SidebarSeatingContainer/SidebarSeatingContainer.props";
import { useSeatingState } from "../../../contexts/useSeatingState";
import {
  formatSeatingObjectsWithTicketInfo,
  getPurchaseLimitsFromSeatingItemArray,
} from "../utils/utils";
import BestAvailableSeatingBody from "./BestAvailableSeatingBody/BestAvailableSeatingBody.web";
import BestAvailableSeatingCartContainer from "./BestAvailableSeatingCart/BestAvailableSeatingCartContainer/BestAvailableSeatingCartContainer.web";
import BestAvailableSeatingHeader from "./BestAvailableSeatingHeader/BestAvailableSeatingHeader.web";

export default function BestAvailableSeating({
  bestAvailableSeating,
  onContinue,
  isLoading,
  onOpenTypeQuantitySelect,
  hideCheckoutButton = false,
  basketFooter,
}: BestAvailableSeatingProps) {
  const {
    seatingItemArray,
    basketType,
    seatingObjectArray,
    seatingEventData,
    synchronizeSeating,
    stageServiceState,
    resetSeating,
  } = useSeatingState();

  const [selectedBestAvailableItemId, setSelectedBestAvailableItemId] =
    useState<string | null>(null);

  const formattedSelectedItems = formatSeatingObjectsWithTicketInfo(
    seatingObjectArray,
    seatingEventData,
    seatingItemArray
  );

  const { min, max } = getPurchaseLimitsFromSeatingItemArray(
    seatingItemArray,
    basketType
  );

  useEffect(() => {
    if (seatingObjectArray.length === 0) {
      setSelectedBestAvailableItemId(null);
    }
  }, [seatingObjectArray]);

  const {
    isOpen: isCartOpen,
    onClose: onCartClose,
    onOpen: onCartOpen,
  } = useDisclosure();

  useEffect(() => {
    if (seatingObjectArray.length > 0) {
      onCartOpen();
    }
  }, [seatingObjectArray, onCartOpen]);

  const handleCloseCart = () => {
    onCartClose();
    resetSeating();
    seatingObjectArray.forEach((item) => {
      if (item.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
        stageServiceState.removeGaSelection(item.id);
      } else {
        stageServiceState.removeSeatSelection(item.id);
      }
      synchronizeSeating();
    });
    setSelectedBestAvailableItemId(null);
  };

  return (
    <Flex direction="column" data-testid="best-available-seating" height="100%">
      {(seatingObjectArray.length === 0 || !isCartOpen) && (
        <>
          <BestAvailableSeatingHeader
            minQuantity={min}
            maxQuantity={max}
            onOpenTypeQuantitySelect={onOpenTypeQuantitySelect}
          />
          <ScrollContainer flex="1">
            <BestAvailableSeatingBody
              bestAvailableSeating={bestAvailableSeating}
              selectedItemId={selectedBestAvailableItemId}
              setSelectedItemId={setSelectedBestAvailableItemId}
              isLoading={isLoading}
              updateSelectedSeats={synchronizeSeating}
              selectedSeatIds={seatingObjectArray.map((item) => item.id)}
            />
          </ScrollContainer>
          {basketFooter}
        </>
      )}

      {seatingObjectArray.length > 0 && isCartOpen && (
        <BestAvailableSeatingCartContainer
          seatingObjectArray={formattedSelectedItems}
          onClose={handleCloseCart}
          onContinue={onContinue}
          isDisabled={
            !selectedBestAvailableItemId && seatingObjectArray.length === 0
          }
          isCartOpen={isCartOpen}
          hideCheckoutButton={hideCheckoutButton}
        />
      )}
    </Flex>
  );
}
